import { Collections, TypeFromCollection } from '@/types/TypeFromCollection';
import {
  collection,
  onSnapshot,
  getDocs,
  getCountFromServer,
  query,
  QueryConstraint,
  CollectionReference,
  DocumentData,
  DocumentSnapshot,
  limit,
  startAfter
} from 'firebase/firestore';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import firestore from '../firestore';
import { handleUnauthenticatedError } from '../handleUnauthenticatedError';
/**
  The useCollection hook is used to get real-time data from a Firestore collection.
  The data is retrieved as an array of objects.
  The function returns the typed data, loading status, and error.
  @param collectionName The name of the collection.
  @param subscribe Whether to subscribe to the collection or not.
  @param queryConstraints The query constraints to apply to the collection.
 * */
export function useCollection<C extends Collections>(
  collectionName: C,
  subscribe = false,
  ...args: Array<boolean | QueryConstraint>
): {
  data: Array<TypeFromCollection<C> & { id: string }>;
  loading: boolean;
  error: string | null;
  refetch: () => void;
} {
  const [enabled, queryConstraints] = useMemo(() => {
    if (typeof args[0] === 'boolean') {
      return [args[0], args.slice(1) as QueryConstraint[]] as const;
    }
    return [true, args as QueryConstraint[]] as const;
  }, [args]);

  const [data, setData] = useState<Array<TypeFromCollection<C> & { id: string }>>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const refetch = useCallback(() => {
    setReloadToken((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(collection(firestore, collectionName), ...queryConstraints);
    if (!subscribe) {
      getDocs(q)
        .then((docs) => {
          setLoading(false);
          if (docs.empty) {
            setData([]);
            return;
          }
          const documents = docs.docs.map((doc) => ({
            id: doc.id,
            ...(doc.data() as TypeFromCollection<C>)
          }));
          setData(documents);
          // eslint-disable-next-line no-console
          // console.log('......fetch done in......', performance.now() - t0, 'ms');
        })
        .catch((e) => {
          setLoading(false);
          setError(e.message);
          handleUnauthenticatedError(e);
          // eslint-disable-next-line no-console
          console.error('Error fetching data:', e);
        });
      return;
    }
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setLoading(false);
        if (snapshot.empty) {
          setData([]);
          return;
        }
        const documents = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as TypeFromCollection<C>)
        }));
        // Keep snapshot handling lightweight for large collections.
        setData(documents);
      },
      (e) => {
        setLoading(false);
        setError(e.message);
        handleUnauthenticatedError(e);
      }
    );
    return () => unsubscribe?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, subscribe, enabled, reloadToken, JSON.stringify(queryConstraints)]);

  return { data, loading, error, refetch };
}

export function useCollectionSimple<C>(
  c: CollectionReference<DocumentData>,
  subscribe = false,
  ...queryConstraints: QueryConstraint[]
): { data: Array<C & { id: string }>; loading: boolean; error: string | null } {
  const [data, setData] = useState<Array<C & { id: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(c, ...queryConstraints);
    if (!subscribe) {
      getDocs(q)
        .then((docs) => {
          setLoading(false);
          if (docs.empty) {
            setData([]);
            return;
          }
          const documents = docs.docs.map((doc) => ({
            id: doc.id,
            ...(doc.data() as C)
          }));
          setData(documents);
        })
        .catch((e) => {
          setLoading(false);
          setError(e.message);
          handleUnauthenticatedError(e);
        });
      return;
    }
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setLoading(false);
        if (snapshot.empty) {
          setData([]);
          return;
        }
        const documents = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as C)
        }));
        setData(documents);
      },
      (e) => {
        setLoading(false);
        setError(e.message);
        handleUnauthenticatedError(e);
      }
    );
    return () => unsubscribe?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.id, subscribe]);

  return { data, loading, error };
}

export async function getCollectionCount<C extends Collections>(
  collectionName: C,
  ...queryConstraints: QueryConstraint[]
): Promise<number> {
  const q = query(collection(firestore, collectionName), ...queryConstraints);
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
}

/**
 * The usePaginatedCollection hook fetches Firestore collection data based on a specific page number and page size.
 *
 * @param collectionName - The name of the Firestore collection.
 * @param pageSize - The number of documents per page.
 * @param pageNumber - The page number to fetch (1-based index).
 * @param queryConstraints - Additional Firestore query constraints.
 * @returns An object containing the paginated data, loading state, error message, total pages, and a function to refetch data.
 */
export function usePaginatedCollection<C extends Collections>(
  collectionName: C,
  pageSize: number,
  pageNumber: number,
  ...queryConstraints: QueryConstraint[]
) {
  const isDev = process.env.NODE_ENV === 'development';
  const [data, setData] = useState<Array<TypeFromCollection<C> & { id: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalDocs, setTotalDocs] = useState(0);
  const stableConstraintsRef = useRef<QueryConstraint[]>(queryConstraints);

  const constraintsChanged =
    stableConstraintsRef.current.length !== queryConstraints.length ||
    stableConstraintsRef.current.some((constraint, idx) => constraint !== queryConstraints[idx]);

  if (constraintsChanged) {
    stableConstraintsRef.current = queryConstraints;
  }

  const stableConstraints = stableConstraintsRef.current;

  useEffect(() => {
    // Avoid showing stale page counts while new filtered count is loading.
    setTotalDocs(0);
  }, [collectionName, stableConstraints]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (isDev) {
      // eslint-disable-next-line no-console
      console.log('[PO_PAGINATION_HOOK] fetchData:start', {
        collectionName,
        pageNumber,
        pageSize,
        constraintsCount: stableConstraints.length
      });
    }

    try {
      // Always refresh total count for current constraints to keep pagination accurate.
      const count = await getCollectionCount(collectionName, ...stableConstraints);
      setTotalDocs(count);

      if (isDev) {
        // eslint-disable-next-line no-console
        console.log('[PO_PAGINATION_HOOK] fetchData:count', {
          collectionName,
          pageNumber,
          count
        });
      }

      // Fetch the starting document for the requested page
      let startDoc: DocumentSnapshot | null = null;
      if (pageNumber > 1) {
        const startQuery = query(
          collection(firestore, collectionName),
          ...stableConstraints,
          limit((pageNumber - 1) * pageSize) // Skip documents up to the required page
        );
        const startSnapshot = await getDocs(startQuery);
        startDoc = startSnapshot.docs[startSnapshot.docs.length - 1] || null;
      }

      // Query for the actual page
      const pageQuery = query(
        collection(firestore, collectionName),
        ...stableConstraints,
        ...(startDoc ? [startAfter(startDoc)] : []),
        limit(pageSize)
      );

      const snapshot = await getDocs(pageQuery);
      if (isDev) {
        // eslint-disable-next-line no-console
        console.log('[PO_PAGINATION_HOOK] fetchData:page', {
          collectionName,
          pageNumber,
          docs: snapshot.docs.length,
          fromCache: snapshot.metadata.fromCache,
          hasPendingWrites: snapshot.metadata.hasPendingWrites
        });
      }
      if (!snapshot.empty) {
        const newDocs = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as TypeFromCollection<C>)
        }));

        setData(newDocs);
      } else {
        setData([]);
      }
    } catch (e: any) {
      setError(e.message);
      handleUnauthenticatedError(e);
      if (isDev) {
        // eslint-disable-next-line no-console
        console.log('[PO_PAGINATION_HOOK] fetchData:error', {
          collectionName,
          pageNumber,
          message: e?.message
        });
      }
    } finally {
      setLoading(false);
      if (isDev) {
        // eslint-disable-next-line no-console
        console.log('[PO_PAGINATION_HOOK] fetchData:end', {
          collectionName,
          pageNumber
        });
      }
    }
  }, [isDev, pageNumber, collectionName, pageSize, stableConstraints]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    totalDocs,
    totalPages: Math.ceil(totalDocs / pageSize),
    refetch: fetchData
  };
}
